import { Link, useParams } from "react-router-dom";
import ArticleCard from "@/components/base/ArticleCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import BackToTop from "@/components/feature/BackToTop";
import PageMeta from "@/components/feature/PageMeta";
import SiteFooter from "@/components/feature/SiteFooter";
import SiteHeader from "@/components/feature/SiteHeader";
import { articles } from "@/mocks/articles";
import { topics } from "@/mocks/topics";
import { formatViews } from "@/utils/format";

export default function TopicPage() {
  const { id } = useParams<{ id: string }>();
  const topic = topics.find((item) => item.id === id);

  if (!topic) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="max-w-3xl mx-auto px-4 md:px-6 pt-32 pb-24 text-center">
          <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-unhappy-line text-3xl"></i>
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">
            没有找到这个分类
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            它可能已经调整或合并，先去首页看看今天的热榜吧。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const list = articles.filter((article) => article.categoryId === topic.id);
  const ranked = [...articles].sort((a, b) => b.heat - a.heat).slice(0, 5);
  const otherTopics = topics.filter((item) => item.id !== topic.id).slice(0, 4);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CollectionPage",
        name: `${topic.name} - 热瓜前线`,
        description: topic.desc,
        url: `https://rnmka.cn/topic/${topic.id}`,
        inLanguage: "zh-CN",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: "https://rnmka.cn/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: topic.name,
            item: `https://rnmka.cn/topic/${topic.id}`,
          },
        ],
      },
      {
        "@type": "ItemList",
        name: `${topic.name}话题列表`,
        itemListElement: list.map((article, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `https://rnmka.cn/article/${article.id}`,
          name: article.title,
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <PageMeta
        title={`${topic.name}｜爆料看料分类聚合 - 热瓜前线`}
        description={`${topic.name}栏目聚合热瓜前线的爆料看料内容：${topic.desc} 当前收录 ${list.length} 篇观察笔记，持续更新网络热议、吃瓜凑热闹与话题复盘。`}
        keywords={`${topic.name},爆料看料,爆料看料围观,吃瓜,热点话题`}
        path={`/topic/${topic.id}`}
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main>
        <section className="bg-background-100 pt-28 md:pt-32 pb-10 md:pb-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[{ label: "首页", to: "/" }, { label: topic.name }]}
            />
            <div className="mt-6 flex flex-col md:flex-row md:items-center gap-5">
              <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700 shrink-0">
                <i className={`${topic.icon} text-2xl md:text-3xl`}></i>
              </span>
              <div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                  {topic.name}
                </h1>
                <p className="mt-2 text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
                  {topic.desc}
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3 text-xs font-label text-foreground-500">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-background-50 border border-background-200">
                <i className="ri-stack-line w-3.5 h-3.5 flex items-center justify-center"></i>
                共 {list.length} 篇内容
              </span>
              <span className="inline-flex items-center gap-1.5">
                更新于 2026 年 9 月
              </span>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <div className="lg:col-span-8">
              {list.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {list.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-foreground-500 font-label">
                  这个分类暂时还没有内容，稍后再来看看吧。
                </p>
              )}
            </div>

            <aside className="lg:col-span-4 flex flex-col gap-8">
              <div className="rounded-lg bg-background-100 border border-background-200/80 p-5">
                <h2 className="font-heading font-bold text-base text-foreground-900 inline-flex items-center gap-2">
                  <i className="ri-fire-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                  全站热榜
                </h2>
                <ol className="mt-4 flex flex-col gap-3">
                  {ranked.map((article, index) => (
                    <li key={article.id}>
                      <Link
                        to={`/article/${article.id}`}
                        className="group flex items-start gap-3 cursor-pointer"
                      >
                        <span
                          className={`w-5 h-5 shrink-0 flex items-center justify-center rounded text-xs font-label font-bold ${
                            index < 3
                              ? "bg-primary-500 text-background-50"
                              : "bg-background-300 text-foreground-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {article.title}
                          </span>
                          <span className="mt-1 inline-flex items-center gap-1 text-xs text-foreground-400 font-label">
                            <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                            {formatViews(article.views)}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg bg-secondary-50 border border-secondary-200/70 p-5">
                <h2 className="font-heading font-bold text-base text-foreground-900">
                  逛逛其他分类
                </h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  {otherTopics.map((item) => (
                    <Link
                      key={item.id}
                      to={`/topic/${item.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50 border border-secondary-200 text-sm font-label text-foreground-700 hover:border-secondary-400 hover:text-secondary-800 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={`${item.icon} w-4 h-4 flex items-center justify-center`}></i>
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}